import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";

interface Answer {
  id: string;
  answerText: string;
  questionId: string;
  question: {
    id: string;
    questionText: string;
  };
}

interface KnowledgeReferenceCardProps {
  answerId: string;
  relevanceScore: number;
}

export default function KnowledgeReferenceCard({
  answerId,
  relevanceScore,
}: KnowledgeReferenceCardProps) {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAnswer() {
      try {
        const response = await fetch(`/api/v1/answers/${answerId}`);
        if (!response.ok) {
          throw new Error("Failed to fetch answer");
        }
        const data = await response.json();
        setAnswer(data);
      } catch (err) {
        console.error("Error fetching answer:", err);
        setError("Failed to load reference");
      } finally {
        setIsLoading(false);
      }
    }

    fetchAnswer();
  }, [answerId]);

  if (isLoading) {
    return (
      <div className="card bg-base-200 shadow-sm">
        <div className="card-body p-4">
          <div className="flex items-center gap-2">
            <span className="loading loading-spinner loading-sm"></span>
            <span className="text-sm text-base-content/70">
              Loading reference...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !answer) {
    return (
      <div className="card bg-error/10 shadow-sm">
        <div className="card-body p-4">
          <p className="text-sm text-error">{error || "Failed to load"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="card-body p-4 space-y-3">
        {/* Relevance Score Badge */}
        <div className="flex justify-between items-start gap-2">
          <div className="badge badge-primary badge-sm">
            Score: {relevanceScore.toLocaleString()}
          </div>
        </div>

        {/* Question */}
        <div>
          <div className="text-xs font-semibold text-base-content/60 uppercase mb-1">
            Question
          </div>
          <a
            href={`/questions/${answer.question.id}`}
            className="link link-hover text-sm font-medium flex items-start gap-1 group"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="flex-1">{answer.question.questionText}</span>
            <ExternalLink className="w-3 h-3 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </a>
        </div>

        {/* Answer */}
        <div>
          <div className="text-xs font-semibold text-base-content/60 uppercase mb-1">
            Answer
          </div>
          <a
            href={`/answers/${answer.id}`}
            className="link link-hover text-sm flex items-start gap-1 group"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="flex-1 line-clamp-3">{answer.answerText}</span>
            <ExternalLink className="w-3 h-3 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </a>
        </div>
      </div>
    </div>
  );
}
